import { Controller } from "@hotwired/stimulus"

// Step 4 · Symbols DataTables with full filter/search/paging + bulk select/delete.
export default class extends Controller {
  static targets = [
    "table",
    "selectAll",
    "rowCheck",
    "bulkForm",
    "bulkFields",
    "bulkDeleteBtn",
    "selectionCount",
    "itemSelect",
    "labelForm",
    "labelInput",
    "thumbBtn",
    "previewModal",
    "previewImage",
    "previewTitle",
    "previewMeta",
    "galleryModal",
    "galleryTitle",
    "galleryMeta",
    "galleryHost",
    "galleryFrame", // legacy alias if any partial still emits it
    "galleryFrameMeta",
    "galleryGrid",
    "gallerySide",
    "galleryDetail",
    "galleryDetailImg",
    "galleryDetailMeta",
    "gallerySelectAll",
    "gallerySelectionCount",
    "galleryKeepBtn",
    "galleryRemoveBtn",
    "galleryBulkLabel",
    "galleryRelabelBtn",
    "galleryRelabelStatus",
    "lazyThumb",
    "cropStage",
    "cropViewport",
    "cropSvg",
    "cropPolyPath",
    "cropPolyLine",
    "cropPolyDots",
    "cropZoomLabel",
    "geomX",
    "geomY",
    "geomW",
    "geomH",
    "geomLegendId",
    "geomForm",
    "formGeomX",
    "formGeomY",
    "formGeomW",
    "formGeomH",
    "formPolygonJson",
    "geomSaveBtn",
    "geomStatus",
    "geomLabel",
    "geomLockLabel"
  ]

  static values = {
    deleteUrl: String,
    itemsSearchUrl: String,
    refineUrl: String,
    acceptUrl: String
  }

  connect() {
    this.boundOnVisible = this.onPanelVisible.bind(this)
    document.addEventListener("cd:panel-opened", this.boundOnVisible)
    this.boundOnStream = this.onBeforeStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundOnStream)
    this._cropPoints = []
    this._cropClosed = false
    this._cropScale = 1
    this._cropPanX = 0
    this._cropPanY = 0
    this._cropPanning = false
    this._cropSpace = false
    this._cropDragVertex = -1
    this._onCropWinMove = (e) => this.cropPointerMove(e)
    this._onCropWinUp = (e) => this.cropPointerUp(e)
    this._onCropKeyDown = (e) => this.cropKeyDown(e)
    this._onCropKeyUp = (e) => this.cropKeyUp(e)
    this._onColPriority = () => this._scheduleColumnPriority()
    this.initTimer = window.setTimeout(() => this.initDataTable(), 10)
    window.addEventListener("resize", this._onColPriority)
    this._thumbObserver = null
    this._thumbDisconnected = false
    this._thumbQueue = new Set()
    this._thumbLoads = new Map()
    this._thumbActive = 0
    this._galleryThumbObserver = null
  }

  disconnect() {
    document.removeEventListener("cd:panel-opened", this.boundOnVisible)
    document.removeEventListener("turbo:before-stream-render", this.boundOnStream)
    window.removeEventListener("resize", this._onColPriority)
    this._unbindCropWindow()
    this._unbindCropPinch()
    this._thumbDisconnected = true
    this._thumbObserver?.disconnect()
    this._thumbQueue?.clear()
    this._thumbLoads?.forEach((cleanup) => cleanup())
    this._thumbLoads?.clear()
    this._thumbActive = 0
    this._galleryThumbObserver?.disconnect()
    if (this.initTimer) {
      window.clearTimeout(this.initTimer)
      this.initTimer = null
    }
    this.hideThumb()
    this.closeGallery()
    this.destroyDataTable()
  }

  // Table sample thumbs: data-src only until visible (stops N PNG renders on open).
  _observeTableThumbs() {
    if (this._thumbDisconnected) return
    if (typeof IntersectionObserver === "undefined") {
      this._loadAllDeferredThumbs()
      return
    }
    this._thumbObserver?.disconnect()
    // Observe the current page inside its actual scrollport, after DataTables
    // has paginated. Off-page rows must never start crop-rendering requests.
    this._thumbQueue?.clear()
    const root = this.element.querySelector(".dt-scroll-body, .dataTables_scrollBody")
    const observer = new IntersectionObserver(
      (entries) => {
        if (this._thumbDisconnected || this._thumbObserver !== observer) return
        entries.forEach((entry) => {
          if (entry.isIntersecting) this._materializeThumb(entry.target)
          else this._thumbQueue?.delete(entry.target)
        })
      },
      { root, rootMargin: "120px 0px", threshold: 0.01 }
    )
    this._thumbObserver = observer
    const thumbs = (root || this.element).querySelectorAll("img.cd-symbol-thumb[data-src]")
    thumbs.forEach((img) => {
      if (img.dataset.src && !img.dataset.loaded) this._thumbObserver.observe(img)
    })
  }

  _materializeThumb(img) {
    if (this._thumbDisconnected || !img || img.dataset.loaded === "1") return
    this._thumbQueue ||= new Set()
    this._thumbQueue.add(img)
    this._drainTableThumbs()
  }

  _drainTableThumbs() {
    if (this._thumbDisconnected) return
    this._thumbQueue ||= new Set()
    this._thumbLoads ||= new Map()
    this._thumbActive ||= 0
    while (this._thumbActive < 4 && this._thumbQueue.size) {
      const img = this._thumbQueue.values().next().value
      this._thumbQueue.delete(img)
      if (!img.isConnected || !this.element.contains(img) || !img.getClientRects().length || img.dataset.loaded === "1") continue
      const src = img.dataset.src
      if (!src) continue
      this._thumbActive += 1
      const cleanup = () => {
        img.removeEventListener("load", done)
        img.removeEventListener("error", done)
      }
      const done = () => {
        if (!this._thumbLoads.has(img)) return
        cleanup()
        this._thumbLoads.delete(img)
        this._thumbActive = Math.max(0, this._thumbActive - 1)
        this._drainTableThumbs()
      }
      this._thumbLoads.set(img, cleanup)
      img.addEventListener("load", done, { once: true })
      img.addEventListener("error", done, { once: true })
      this._thumbObserver?.unobserve(img)
      img.src = src
      img.dataset.loaded = "1"
      img.classList.remove("is-deferred")
      img.removeAttribute("data-src")
    }
  }

  _loadAllDeferredThumbs() {
    const thumbs = this.element.querySelectorAll("img.cd-symbol-thumb[data-src]")
    thumbs.forEach((img) => this._materializeThumb(img))
  }

  // Keep DataTables' row cache in sync with turbo_stream remove/replace on
  // OUR rows. Raw DOM swaps behind DataTables' back corrupt its index —
  // deleted rows resurrect on the next draw, replaced rows go stale in
  // search/sort. We take over rendering for row-targeted streams.
  onBeforeStreamRender(event) {
    const stream = event.target
    if (!stream || typeof stream.getAttribute !== "function") return
    const action = stream.getAttribute("action")
    if (!["remove", "replace", "append"].includes(action)) return
    const targetId = stream.getAttribute("target")
    if (!targetId || !this.dataTable) return
    if (action === "append") {
      if (targetId !== this.tableEl()?.querySelector("tbody")?.id) return
      const rows = [...(stream.querySelector("template")?.content?.querySelectorAll("tr") || [])]
      if (!rows.length) return
      event.preventDefault()
      this.dataTable.rows.add(rows).draw(false)
      this.syncReviewStats()
      this.syncSelectionUi()
      return
    }
    // Paged-out rows are detached from document, but still belong to DataTables.
    const node = this.dataTable.rows().nodes().toArray().find((row) => row.id === targetId)
    if (!node) return

    if (action === "remove") {
      event.preventDefault()
      this.destroyItemSelect2(node.querySelectorAll(".cd-symbol-item-select"))
      try {
        // Drop from DataTables' cache first, then from the DOM. No draw() —
        // the current page, sort, and scroll position must not move.
        this.dataTable.row(node).remove()
      } catch (_error) {
        // ignore — still remove the node
      }
      node.remove()
      this.syncSelectionUi()
      this.syncReviewStats()
      return
    }

    // Preserve DataTables' cached TR and TD nodes (including hidden columns).
    // Replacing innerHTML on the TR leaves its cell cache pointing at old forms.
    // No draw() here: saving an Item must not move the current page or scroll.
    const newTr = stream.querySelector("template")?.content?.querySelector("tr")
    if (!newTr) return
    const cells = this.dataTable.cells(node, this.dataTable.columns().indexes().toArray()).nodes().toArray()
    if (cells.length !== newTr.cells.length) return
    event.preventDefault()
    this.destroyItemSelect2(node.querySelectorAll(".cd-symbol-item-select"))
    const copyAttributes = (target, source) => {
      ;[...target.attributes].forEach((attr) => {
        if (!source.hasAttribute(attr.name)) target.removeAttribute(attr.name)
      })
      ;[...source.attributes].forEach((attr) => target.setAttribute(attr.name, attr.value))
    }
    copyAttributes(node, newTr)
    cells.forEach((cell, index) => {
      copyAttributes(cell, newTr.cells[index])
      cell.innerHTML = newTr.cells[index].innerHTML
    })
    try {
      this.dataTable.row(node).invalidate("dom")
    } catch (_error) {
      // ignore
    }
    this.initItemSelect2()
    this.syncSelectionUi()
    this.syncReviewStats()
    this._observeTableThumbs()
  }

  syncReviewStats() {
    const stats = this.element.closest("#symbols")?.querySelector(".cd-stats")
    if (!stats || !this.dataTable) return
    const rows = this.dataTable.rows().nodes().toArray()
    const reviewed = rows.filter(row => row.dataset.groupNeedsReview === "false")
    const unresolved = rows.filter(row => row.dataset.groupNeedsReview === "true")
    const unmapped = reviewed.filter(row => row.dataset.groupMapped !== "true")
    const count = list => list.reduce((sum, row) => sum + Number(row.dataset.groupCount || 0), 0)
    const values = {
      candidate_count: count(rows), identified_count: count(reviewed), review_count: count(unresolved),
      label_count: rows.length, mapped_count: reviewed.length - unmapped.length, map_unmapped: unmapped.length
    }
    Object.entries(values).forEach(([key, value]) => {
      const node = stats.querySelector(`[id^="symbols_${key}_"]`)
      if (node) node.textContent = String(value)
    })
    stats.querySelector('[id^="symbols_review_count_"]')?.classList.toggle("cd-stats-warn", unresolved.length > 0)
    const details = stats.querySelector("details.cd-stats-unmapped")
    if (details) details.hidden = unmapped.length === 0
    const list = details?.querySelector("ul")
    if (list) list.replaceChildren(...unmapped.map(row => {
      const entry = document.createElement("li")
      const code = document.createElement("code")
      code.textContent = row.dataset.groupLabel || ""
      entry.append(code)
      return entry
    }))
  }

  onPanelVisible(event) {
    const section = event?.detail?.section
    if (section && section !== "symbols") return
    if (!this.element.isConnected || !this.element.closest("#symbols")) return
    this.adjust()
  }

  adjust() {
    if (!this.dataTable) {
      this.initDataTable()
      return
    }
    try {
      this.dataTable.columns.adjust()
      if (typeof this.dataTable.draw === "function") this.dataTable.draw(false)
    } catch (_error) {
      this.destroyDataTable()
      this.initDataTable()
    }
  }

  initDataTable() {
    if (this.initializing) return
    const table = this.tableEl()
    if (!table || !table.isConnected) return
    if (!window.$?.fn?.DataTable) return

    this.initializing = true
    try {
      this.destroyDataTable()
      this.destroyOrphansByTableId(table.id)

      if ($.fn.DataTable.isDataTable(table)) {
        try {
          $(table).DataTable().destroy()
        } catch (_error) {
          // ignore
        }
      }

      if ($.fn.DataTable.isDataTable(table)) {
        this.dataTable = $(table).DataTable()
        this.bindTableEvents()
        this.installColumnFilters()
        this.syncSelectionUi()
        this.initItemSelect2()
        this._observeTableThumbs()
        return
      }

      const prevErrMode = $.fn.dataTable?.ext?.errMode
      if ($.fn.dataTable?.ext) $.fn.dataTable.ext.errMode = "none"

      try {
        // DataTables must own both header rows so hidden columns stay aligned.
        this.installColumnFilters()
        this.dataTable = $(table).DataTable({
          // Full feature set: length + global search + table + info + paging
          dom:
            "<'cd-dt-top'<'cd-dt-length'l><'cd-dt-filter'f>>" +
            "t" +
            "<'cd-dt-bottom'<'cd-dt-info'i><'cd-dt-page'p>>",
          paging: true,
          pageLength: 25,
          lengthMenu: [
            [25, 50, 100, 250, -1],
            [25, 50, 100, 250, "All"]
          ],
          searching: true,
          ordering: true,
          info: true,
          autoWidth: false,
          order: [[2, "desc"]],
          orderFixed: { pre: [[0, "asc"]] },
          scrollY: "62vh",
          scrollCollapse: true,
          scrollX: true,
          search: {
            smart: false,
            regex: false,
            caseInsensitive: true
          },
          language: {
            search: "Search all:",
            searchPlaceholder: "Master, sources, item, pages…",
            lengthMenu: "Show _MENU_ groups",
            info: "Showing _START_–_END_ of _TOTAL_ groups",
            infoEmpty: "No label groups",
            infoFiltered: "(filtered from _MAX_ total)",
            zeroRecords: "No matching labels",
            paginate: {
              first: "First",
              last: "Last",
              next: "Next",
              previous: "Prev"
            }
          },
          // 0 check · 1 glyph · 2 qty · 3 master · 4 sources · 5 item · 6 pages · 7 actions
          columnDefs: this._columnDefsForTable(table)
        })
      } finally {
        if ($.fn.dataTable?.ext && prevErrMode !== undefined) {
          $.fn.dataTable.ext.errMode = prevErrMode
        }
      }

      this.bindTableEvents()
      this.syncSelectionUi()
      this.initItemSelect2()
      this._applyColumnPriority()

      window.requestAnimationFrame(() => {
        try {
          if (this.dataTable && this.element.isConnected) {
            this.dataTable.columns.adjust()
            this._observeTableThumbs()
          }
        } catch (_error) {
          // ignore
        }
      })
    } catch (error) {
      console.warn("[cd-symbols-table] DataTable init failed", error)
      this.dataTable = null
    } finally {
      this.initializing = false
    }
  }

  // Column map: 0 check, 1 glyph, 2 qty, 3 master, 4 sources, 5 item, 6 pages, 7 actions.
  _columnLayout(table) {
    const n = this.dataTable
      ? this.dataTable.columns().count()
      : table?.querySelector("thead tr")?.children.length || 0
    const sources = n >= 8 ? 4 : -1
    return {
      count: n,
      sources,
      steps: sources >= 0 ? [sources] : [],
      glyph: 1,
      master: 3,
      pages: Math.max(0, n - 2),
      item: Math.max(0, n - 3),
      actions: Math.max(0, n - 1)
    }
  }

  _columnDefsForTable(table) {
    const layout = this._columnLayout(table)
    const defs = [
      { orderable: false, searchable: false, targets: 0, width: "36px" },
      { orderable: false, searchable: true, targets: 1, width: "72px" },
      { orderable: true, type: "num", targets: 2, width: "52px" },
      { orderable: true, targets: 3, width: "220px" }
    ]
    if (layout.sources >= 0) {
      defs.push({ orderable: true, targets: layout.sources, width: "280px" })
    }
    if (layout.count >= 3) {
      defs.push({ orderable: true, targets: layout.item, width: "220px" })
      defs.push({ orderable: true, targets: layout.pages, width: "72px" })
      defs.push({ orderable: false, searchable: false, targets: layout.actions, width: "88px" })
    }
    return defs
  }

  _scheduleColumnPriority() {
    if (this._colPriTimer) window.clearTimeout(this._colPriTimer)
    this._colPriTimer = window.setTimeout(() => this._applyColumnPriority(), 120)
  }

  // Narrow screens: hide sources last. Master + Item + Qty stay visible.
  _applyColumnPriority() {
    if (!this.dataTable) return
    const table = this.dataTable.table().node()
    if (!table) return
    const layout = this._columnLayout(table)
    const w = window.innerWidth || document.documentElement.clientWidth || 1200
    // >=800: sources · <700 hide pages · <420 hide glyph
    const showSteps = w >= 800
    const showPages = w >= 700
    const showGlyph = w >= 420
    try {
      layout.steps.forEach((idx) => {
        this.dataTable.column(idx).visible(showSteps, false)
      })
      if (layout.pages >= 0) this.dataTable.column(layout.pages).visible(showPages, false)
      if (layout.glyph >= 0) this.dataTable.column(layout.glyph).visible(showGlyph, false)
      this.dataTable.columns.adjust().draw(false)
    } catch (err) {
      console.warn("[cd-symbols-table] column priority failed", err)
    }
  }

  // Per-column filter inputs under the header (Count / Label / Item / Pages).
  installColumnFilters() {
    const table = this.tableEl()
    if (!table) return

    const thead = this.dataTable ? this.dataTable.table().header() : table.querySelector("thead")
    if (!thead) return
    if (thead.querySelector("tr.cd-dt-col-filters")) return

    const headerRow = thead.querySelector("tr")
    if (!headerRow) return
    const filterRow = document.createElement("tr")
    filterRow.className = "cd-dt-col-filters"
    filterRow.dataset.dtOrder = "disable"

    // 0 check · 1 glyph · 2 qty · 3 master · 4 sources · 5 item · 6 pages · 7 actions
    const layout = this._columnLayout(table)
    const placeholders = {
      2: "qty",
      3: "master…",
      4: "sources…",
      5: "item…",
      6: "pages…"
    }
    ;[...headerRow.children].forEach((th, idx) => {
      const cell = document.createElement("th")
      cell.className = th.className || ""
      if (idx === 0 || idx === layout.glyph || idx === layout.actions || !placeholders[idx]) {
        cell.innerHTML = ""
      } else {
        const input = document.createElement("input")
        input.type = "search"
        input.className = "form-control form-control-sm cd-dt-col-filter"
        input.placeholder = placeholders[idx] || "filter…"
        input.setAttribute("aria-label", `Filter column ${idx}`)
        input.addEventListener("click", (e) => e.stopPropagation())
        input.addEventListener("keydown", (e) => e.stopPropagation())
        input.addEventListener("input", () => {
          const val = input.value
          // Literal contains (DataTable smart search already disabled globally).
          this.dataTable?.column(idx).search(val, false, false).draw()
        })
        cell.appendChild(input)
      }
      filterRow.appendChild(cell)
    })
    thead.appendChild(filterRow)
  }

  bindTableEvents() {
    if (!this.dataTable) return
    // DataTables triggers namespaced events: a draw handler must include .dt.
    $(this.dataTable.table().node()).off(".cdSymbols").on("preDraw.dt.cdSymbols", () => {
      this.element.querySelectorAll(".cd-symbol-item-select").forEach((el) => {
        if ($(el).data("select2")) $(el).select2("close")
      })
    }).on("draw.dt.cdSymbols", () => {
      this.syncSelectionUi()
      // Initialize selectors for the current page without opening their menus.
      this.initItemSelect2()
      // DataTables re-uses/clones rows — re-observe deferred thumbs.
      window.requestAnimationFrame(() => this._observeTableThumbs())
    })
  }

  // —— Item catalog Select2 (AJAX). Always on visible rows, not native <select>. ——

  initItemSelect2() {
    const $ = window.jQuery || window.$
    if (!($ && typeof $.fn?.select2 === "function")) return
    if (!this.hasItemsSearchUrlValue || !this.itemsSearchUrlValue) return

    let nodes = []
    if (this.dataTable) {
      try {
        const body = this.dataTable.table().body()
        nodes = [...body.querySelectorAll(".cd-symbol-item-select")]
      } catch (_error) {
        nodes = []
      }
    }
    if (!nodes.length) {
      nodes = [...this.element.querySelectorAll("tbody .cd-symbol-item-select")]
    }
    nodes.forEach((el) => this._ensureSelect2(el))
  }

  _ensureSelect2(el) {
    const $ = window.jQuery || window.$
    if (!($ && typeof $.fn?.select2 === "function")) return
    if (!this.hasItemsSearchUrlValue || !this.itemsSearchUrlValue) return
    if (!el || !el.isConnected) return
    const $el = $(el)
    if ($el.data("select2")) return

    try {
      $el.select2({
        width: "100%",
        theme: "default",
        placeholder: el.dataset.placeholder || "Map to Item…",
        allowClear: true,
        dropdownParent: $(document.body),
        minimumInputLength: 0,
        ajax: {
          url: this.itemsSearchUrlValue,
          dataType: "json",
          delay: 200,
          data: (params) => ({
            q: params.term || "",
            page: params.page || 1
          }),
          processResults: (data) => ({
            results: Array.isArray(data?.results) ? data.results : [],
            pagination: { more: !!data?.pagination?.more }
          }),
          cache: true
        }
      })

      $el.off("change.cdSymbolItem").on("change.cdSymbolItem", () => {
        const form = el.closest("form.cd-symbol-item-form")
        if (!form || form.dataset.submitting === "1") return
        form.dataset.submitting = "1"
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit()
        } else {
          form.submit()
        }
        window.setTimeout(() => {
          if (form.isConnected) form.dataset.submitting = "0"
        }, 1500)
      })
    } catch (error) {
      console.warn("[cd-symbols-table] Select2 init failed", error)
    }
  }

  destroyItemSelect2(selects = null) {
    const $ = window.jQuery || window.$
    if (!($ && typeof $.fn?.select2 === "function")) return
    // Stimulus targets only include connected rows; visit the cached pages too.
    if (!selects) {
      selects = new Set(this.element.querySelectorAll(".cd-symbol-item-select"))
      this.dataTable?.rows().nodes().toArray().forEach((row) => {
        row.querySelectorAll(".cd-symbol-item-select").forEach((el) => selects.add(el))
      })
    }
    selects.forEach((el) => {
      try {
        const $el = $(el)
        $el.off("change.cdSymbolItem")
        if ($el.data("select2")) $el.select2("destroy")
      } catch (_error) {
        // ignore
      }
    })
  }

  // —— Label form save (blur / Enter). DataTables must not swallow submits. ——

  submitLabelForm(event) {
    if (event?.type === "keydown") {
      event.preventDefault()
    }
    const input = event?.currentTarget || event?.target
    if (!input || input.disabled) return
    const form = input.closest("form.cd-symbol-save-form")
    if (!form || form.dataset.submitting === "1") return

    const value = (input.value || "").trim()
    if (!value) {
      input.reportValidity?.()
      return
    }

    // Skip no-op resubmit of the same committed value.
    if (input.dataset.lastSaved === value && event?.type === "change") return

    form.dataset.submitting = "1"
    input.dataset.lastSaved = value
    // ALWAYS Turbo: requestSubmit fires the submit event Turbo listens for.
    // Native form.submit() bypasses Turbo and does a full document navigation.
    form.setAttribute("data-turbo", "true")
    form.setAttribute("data-turbo-stream", "true")
    try {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit()
      } else {
        // Fallback that still goes through Turbo's submit handler.
        form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
      }
    } finally {
      window.setTimeout(() => {
        if (form.isConnected) form.dataset.submitting = "0"
      }, 2000)
    }
  }

  // —— Symbol thumbnail modal ——

  showThumb(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    const btn = event?.currentTarget
    if (!btn || !this.hasPreviewModalTarget) return
    const url = btn.dataset.thumbUrl
    if (!url) return

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = url
      this.previewImageTarget.alt = btn.dataset.thumbLabel || "Symbol preview"
    }
    if (this.hasPreviewTitleTarget) {
      this.previewTitleTarget.textContent = btn.dataset.thumbLabel || "Symbol"
    }
    if (this.hasPreviewMetaTarget) {
      const count = btn.dataset.thumbCount || ""
      const pages = btn.dataset.thumbPages || ""
      const status = btn.dataset.thumbStatus || ""
      this.previewMetaTarget.textContent = [count ? `${count} candidate(s)` : null, pages || null, status || null]
        .filter(Boolean)
        .join(" · ")
    }
    this.previewModalTarget.hidden = false
    document.body.classList.add("cd-symbol-preview-open")
  }

  hideThumb(event) {
    event?.preventDefault?.()
    if (!this.hasPreviewModalTarget) return
    this.previewModalTarget.hidden = true
    document.body.classList.remove("cd-symbol-preview-open")
    if (this.hasPreviewImageTarget) this.previewImageTarget.removeAttribute("src")
  }

  // —— Single shared gallery modal: fetch HTML for ONE group into host (NO turbo-frame) ——

  _galleryHostEl() {
    if (this.hasGalleryHostTarget) return this.galleryHostTarget
    if (this.hasGalleryFrameTarget) return this.galleryFrameTarget
    return null
  }

  openGallery(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    const btn = event?.currentTarget
    if (!btn || !this.hasGalleryModalTarget) return

    const ids = (btn.dataset.symbolIds || "").split(",").map((s) => s.trim()).filter(Boolean)
    const label = btn.dataset.galleryLabel || "Crops"
    const count = btn.dataset.galleryCount || String(ids.length)
    const base = btn.dataset.galleryUrl || btn.dataset.instancesUrl
    if (!base || !ids.length) return

    const qs = new URLSearchParams()
    ids.forEach((id) => qs.append("symbol_ids[]", id))
    let url = base
    if (!url.includes("symbol_review_gallery") && url.includes("symbol_instances")) {
      url = url.replace(/symbol_instances.*/, "symbol_review_gallery")
    }
    url = `${url}${url.includes("?") ? "&" : "?"}${qs.toString()}`

    if (this.hasGalleryTitleTarget) this.galleryTitleTarget.textContent = label
    if (this.hasGalleryMetaTarget) {
      this.galleryMetaTarget.textContent = `Loading ${count} instance(s)…`
    }
    this._clearGalleryPageImage()
    this._galleryThumbObserver?.disconnect()
    if (this.hasGalleryDetailTarget) this.galleryDetailTarget.hidden = true
    this.galleryModalTarget.hidden = false
    document.body.classList.add("cd-gallery-open")
    this._gallerySelectedId = null
    this._gallerySession = {}
    this._galleryRangeAnchorId = null
    if (this.hasGalleryBulkLabelTarget) this.galleryBulkLabelTarget.value = ""
    this._setGalleryRelabelStatus("")
    this._galleryCheckedIds = new Set()
    this._galleryGroupIds = ids.slice()
    this._cropDraft = null
    this.syncGallerySelectionUi()

    const pathMatch = url.match(/(\/construction_drawings\/\d+)/)
    this._galleryDrawingBase = pathMatch ? pathMatch[1] : null
    if (this.hasGeomFormTarget && this._galleryDrawingBase) {
      this.geomFormTarget.action = `${this._galleryDrawingBase}/update_symbol_geometry`
    }

    // Always fetch partial HTML into shared host — never turbo-frame / full page reload.
    this._fetchGalleryHtml(url)
  }

  galleryFrameLoaded(_event) {
    // Legacy no-op (turbo-frame removed).
    this._afterGalleryContentLoaded()
  }

  async _fetchGalleryHtml(url) {
    this._galleryRequest?.abort()
    const request = new AbortController()
    this._galleryRequest = request
    const host = this._galleryHostEl()
    if (!host) {
      if (this.hasGalleryMetaTarget) this.galleryMetaTarget.textContent = "Gallery host missing."
      return
    }
    host.innerHTML =
      `<div class="cd-gallery-grid" aria-busy="true"><p class="cd-dim cd-gallery-empty-hint">Loading crops…</p></div>`
    try {
      const res = await fetch(url, {
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        signal: request.signal
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      if (request !== this._galleryRequest || request.signal.aborted || this.galleryModalTarget.hidden) return
      const tmp = document.createElement("div")
      tmp.innerHTML = html
      // Strip any accidental turbo-frame wrappers from older servers.
      const frame = tmp.querySelector("turbo-frame")
      host.innerHTML = frame ? frame.innerHTML : tmp.innerHTML
      this._afterGalleryContentLoaded()
    } catch (err) {
      if (request !== this._galleryRequest || request.signal.aborted) return
      console.warn("[cd-symbols-table] gallery load failed", err)
      host.innerHTML =
        `<div class="cd-gallery-grid"><p class="cd-dim">Failed to load instances.</p></div>`
      if (this.hasGalleryMetaTarget) this.galleryMetaTarget.textContent = "Failed to load instances."
    } finally {
      if (request === this._galleryRequest) this._galleryRequest = null
    }
  }

  _afterGalleryContentLoaded() {
    const meta = this._galleryHostEl()?.querySelector("[data-gallery-count]")
    if (meta && this.hasGalleryMetaTarget) {
      const n = meta.dataset.galleryCount || ""
      const lab = meta.dataset.galleryLabel || ""
      this.galleryMetaTarget.textContent =
        `${n || "0"} crop(s)${lab ? ` · ${lab}` : ""} · select to relabel, keep or remove`
    }
    this._observeGalleryThumbs()
    this.syncGallerySelectionUi()
  }

  _observeGalleryThumbs() {
    const root = this._galleryHostEl() || this.element
    const imgs = root.querySelectorAll("img.cd-gallery-tile-img[data-src], img[data-src].cd-gallery-tile-img")
    if (!imgs.length) return
    if (typeof IntersectionObserver === "undefined") {
      imgs.forEach((img) => this._materializeGalleryThumb(img))
      return
    }
    this._galleryThumbObserver?.disconnect()
    this._galleryThumbObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          this._materializeGalleryThumb(entry.target)
          this._galleryThumbObserver.unobserve(entry.target)
        })
      },
      {
        root: this._galleryHostEl() || null,
        rootMargin: "80px",
        threshold: 0.01
      }
    )
    imgs.forEach((img) => this._galleryThumbObserver.observe(img))
  }

  _materializeGalleryThumb(img) {
    if (!img || img.isConnected === false || img.dataset.loaded === "1" || this.galleryModalTarget.hidden) return
    const src = img.dataset.src
    if (!src) return
    const fallback = img.dataset.srcFallback
    img.onerror = () => {
      if (fallback && img.dataset.fallbackTried !== "1" && img.src !== new URL(fallback, document.baseURI).href) {
        img.dataset.fallbackTried = "1"
        img.src = fallback
      } else {
        img.style.display = "none"
        img.closest(".cd-gallery-tile")?.classList.add("is-broken")
      }
    }
    img.onload = () => {
      img.style.opacity = "1"
      img.closest(".cd-gallery-tile")?.classList.add("has-thumbnail")
      img.closest(".cd-gallery-tile")?.classList.remove("is-broken")
    }
    img.src = src
    img.dataset.loaded = "1"
    img.removeAttribute("data-src")
  }

  // Event delegation for Turbo-injected tiles (no per-tile listeners).
  galleryGridClick(event) {
    const check = event.target.closest?.(".cd-gallery-tile-check")
    if (check) {
      event.stopPropagation()
      this._checkGalleryRange(check, event.shiftKey)
      return
    }
    const body = event.target.closest?.("[data-gallery-select], .cd-gallery-tile-body")
    if (!body) return
    const tile = body.closest(".cd-gallery-tile")
    if (!tile) return
    event.preventDefault()
    if (event.shiftKey) {
      this._checkGalleryRange(tile.querySelector(".cd-gallery-tile-check"), true, true)
      return
    }
    this.selectGalleryTile(event, tile)
  }

  galleryGridChange(event) {
    const check = event.target.closest?.(".cd-gallery-tile-check")
    if (!check) return
    this.galleryCheckChanged(event)
  }

  _escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  // Uniform tile images: try URL list in order; on total failure keep placeholder.
  _wireGalleryTileImg(img, tile, urls) {
    const list = Array.from(new Set((urls || []).filter(Boolean)))
    img.dataset.urlList = JSON.stringify(list)
    img.dataset.urlIndex = "0"
    img.addEventListener("error", () => this._onGalleryTileImgError(img, tile))
    img.addEventListener("load", () => {
      tile.classList.remove("is-broken")
      tile.classList.add("has-thumbnail")
      img.style.display = ""
      img.style.opacity = "1"
    })
  }

  _onGalleryTileImgError(img, tile) {
    let list = []
    try {
      list = JSON.parse(img.dataset.urlList || "[]")
    } catch (_e) {
      list = []
    }
    let idx = parseInt(img.dataset.urlIndex || "0", 10) + 1
    img.dataset.urlIndex = String(idx)
    if (idx < list.length) {
      img.src = list[idx]
      return
    }
    // All URLs failed — show placeholder only
    tile.classList.add("is-broken")
    img.style.display = "none"
    img.removeAttribute("src")
  }

  _observeGalleryImg(img) {
    if (!("IntersectionObserver" in window)) {
      if (img.dataset.src) img.src = img.dataset.src
      return
    }
    if (!this._galleryObserver) {
      this._galleryObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return
            const el = en.target
            if (el.dataset.src) {
              el.src = el.dataset.src
              delete el.dataset.src
            }
            this._galleryObserver.unobserve(el)
          })
        },
        { root: this.hasGalleryGridTarget ? this.galleryGridTarget : null, rootMargin: "80px" }
      )
    }
    this._galleryObserver.observe(img)
  }

  galleryCheckChanged(event) {
    const el = event?.target
    if (!el) return
    const id = el.dataset.instanceId
    if (!id) return
    if (!this._galleryCheckedIds) this._galleryCheckedIds = new Set()
    if (el.checked) this._galleryCheckedIds.add(id)
    else this._galleryCheckedIds.delete(id)
    const tile = el.closest(".cd-gallery-tile")
    if (tile) tile.classList.toggle("is-checked", el.checked)
    this.syncGallerySelectionUi()
  }

  _checkGalleryRange(check, extend, checked = check?.checked) {
    if (!check || this._galleryRelabelInFlight) return
    const checks = [...this.galleryGridTarget.querySelectorAll(".cd-gallery-tile-check")]
    const last = checks.indexOf(check)
    const anchor = checks.findIndex(input => input.dataset.instanceId === this._galleryRangeAnchorId)
    if (last < 0) return
    const first = extend && anchor >= 0 ? anchor : last
    if (!extend || anchor < 0) this._galleryRangeAnchorId = check.dataset.instanceId
    this._galleryCheckedIds ||= new Set()
    checks.slice(Math.min(first, last), Math.max(first, last) + 1).forEach(input => {
      input.checked = checked
      const id = input.dataset.instanceId
      if (checked) this._galleryCheckedIds.add(id)
      else this._galleryCheckedIds.delete(id)
      input.closest(".cd-gallery-tile")?.classList.toggle("is-checked", checked)
    })
    this.syncGallerySelectionUi()
  }

  galleryToggleSelectAll(event) {
    const on = !!event?.target?.checked
    if (!this._galleryCheckedIds) this._galleryCheckedIds = new Set()
    this.galleryGridTarget?.querySelectorAll(".cd-gallery-tile-check")?.forEach((el) => {
      el.checked = on
      const id = el.dataset.instanceId
      if (!id) return
      if (on) this._galleryCheckedIds.add(id)
      else this._galleryCheckedIds.delete(id)
      el.closest(".cd-gallery-tile")?.classList.toggle("is-checked", on)
    })
    this.syncGallerySelectionUi()
  }

  syncGallerySelectionUi() {
    const n = this._galleryCheckedIds?.size || 0
    const saving = !!(this._galleryRelabelInFlight || this._galleryGeometrySaving)
    if (this.hasGalleryRelabelBtnTarget) {
      this.galleryRelabelBtnTarget.disabled = saving || !n || !this.galleryBulkLabelTarget.value.trim()
      this.galleryRelabelBtnTarget.textContent = saving ? "Saving…" : "Relabel selected"
    }
    if (this.hasGalleryBulkLabelTarget) this.galleryBulkLabelTarget.disabled = saving
    if (this.hasGalleryKeepBtnTarget) this.galleryKeepBtnTarget.disabled = saving || !n
    if (this.hasGalleryRemoveBtnTarget) this.galleryRemoveBtnTarget.disabled = saving || !n
    if (this.hasGeomSaveBtnTarget) this.geomSaveBtnTarget.disabled = saving || !this._galleryImageReady
    if (this.hasGallerySelectionCountTarget) {
      this.gallerySelectionCountTarget.textContent = `${n} selected`
    }
    if (this.hasGallerySelectAllTarget && this.hasGalleryGridTarget) {
      const checks = [...this.galleryGridTarget.querySelectorAll(".cd-gallery-tile-check")]
      checks.forEach(check => { check.disabled = saving })
      this.gallerySelectAllTarget.disabled = saving
      const allOn = checks.length > 0 && checks.every((c) => c.checked)
      const some = checks.some((c) => c.checked)
      this.gallerySelectAllTarget.checked = allOn
      this.gallerySelectAllTarget.indeterminate = some && !allOn
    }
  }

  galleryCheckedIds() {
    return [...(this._galleryCheckedIds || [])]
  }

  _setGalleryRelabelStatus(message, failed = false) {
    if (!this.hasGalleryRelabelStatusTarget) return
    this.galleryRelabelStatusTarget.textContent = message
    this.galleryRelabelStatusTarget.hidden = !message
    this.galleryRelabelStatusTarget.classList.toggle("cd-stats-warn", failed)
  }

  async galleryRelabelSelected(event) {
    event?.preventDefault?.()
    if (this._galleryRelabelInFlight || this._galleryGeometrySaving) return
    const ids = this.galleryCheckedIds()
    const label = this.galleryBulkLabelTarget.value.trim()
    if (!ids.length || !label || !this.hasRefineUrlValue) return
    const session = this._gallerySession
    const body = new URLSearchParams({ label, review_gallery: "1" })
    ids.forEach(id => body.append("symbol_ids[]", id))
    this._galleryRelabelInFlight = true
    this.syncGallerySelectionUi()
    this._setGalleryRelabelStatus(`Relabeling ${ids.length} selected crop(s)…`)
    try {
      const response = await fetch(this.refineUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
        },
        body: body.toString(),
        credentials: "same-origin"
      })
      const result = await response.json().catch(() => ({error: "Unable to relabel the selected crops. Please retry."}))
      if (!response.ok || !result.ok) throw new Error(result.error || "Unable to relabel the selected crops.")
      // Patch only affected table groups. Keep this modal, its scroll and its
      // already-loaded thumbnails/canvas intact.
      if (result.streams) await window.Turbo.renderStreamMessage(result.streams)
      if (session !== this._gallerySession || this.galleryModalTarget.hidden) return
      const changed = new Set(result.symbol_ids.map(String))
      const savedLabel = result.label
      this.galleryGridTarget.querySelectorAll(".cd-gallery-tile").forEach(tile => {
        if (!changed.has(tile.dataset.instanceId)) return
        tile.dataset.label = savedLabel
        tile.title = `p.${tile.dataset.page} · #${tile.dataset.instanceId} · ${savedLabel}`
        const caption = tile.querySelector(".cd-gallery-tile-cap")
        if (caption) {
          caption.textContent = `p.${tile.dataset.page} · ${savedLabel.length > 14 ? `${savedLabel.slice(0, 13)}…` : savedLabel}`
          const status = document.createElement("small")
          status.className = "d-block"
          status.textContent = "Manual label"
          caption.append(status)
        }
        const placeholder = tile.querySelector(".cd-gallery-tile-placeholder strong")
        if (placeholder) placeholder.textContent = savedLabel
        const image = tile.querySelector(".cd-gallery-tile-img")
        if (image) image.alt = savedLabel
        const check = tile.querySelector(".cd-gallery-tile-check")
        if (check) check.checked = false
        tile.classList.remove("is-checked")
        this._galleryCheckedIds.delete(tile.dataset.instanceId)
      })
      if (changed.has(String(this._gallerySelectedId))) {
        if (this.hasGeomLabelTarget) this.geomLabelTarget.value = savedLabel
        if (this.hasGeomLockLabelTarget) this.geomLockLabelTarget.checked = true
        if (this.hasGalleryDetailMetaTarget) this.galleryDetailMetaTarget.textContent = `Instance #${this._gallerySelectedId} · ${savedLabel}`
      }
      const labels = new Set([...this.galleryGridTarget.querySelectorAll(".cd-gallery-tile")].map(tile => tile.dataset.label))
      if (this.hasGalleryTitleTarget) this.galleryTitleTarget.textContent = labels.size === 1 ? savedLabel : "Review crops · multiple labels"
      if (this.hasGalleryMetaTarget) this.galleryMetaTarget.textContent = `${this.galleryGridTarget.querySelectorAll(".cd-gallery-tile").length} crop(s) · select to relabel, keep or remove`
      this._setGalleryRelabelStatus(`Relabeled ${changed.size} crop(s) as “${savedLabel}”.`)
    } catch (error) {
      if (session === this._gallerySession && !this.galleryModalTarget.hidden) {
        this._setGalleryRelabelStatus(error.message || "Unable to relabel the selected crops.", true)
      }
    } finally {
      this._galleryRelabelInFlight = false
      this.syncGallerySelectionUi()
    }
  }

  async galleryRemoveSelected(event) {
    event?.preventDefault?.()
    if (this._galleryRelabelInFlight) return
    const ids = this.galleryCheckedIds()
    if (!ids.length) {
      window.alert("Select one or more tiles to remove.")
      return
    }
    if (!window.confirm(`Remove ${ids.length} selected crop(s)? This cannot be undone easily.`)) return
    await this._galleryDeleteIds(ids)
  }

  async galleryKeepSelected(event) {
    event?.preventDefault?.()
    if (this._galleryRelabelInFlight) return
    const keep = new Set(this.galleryCheckedIds())
    if (!keep.size) {
      window.alert("Select the crops you want to KEEP first.")
      return
    }
    const all = [...this.galleryGridTarget.querySelectorAll(".cd-gallery-tile")].map((t) => t.dataset.instanceId).filter(Boolean)
    const remove = all.filter((id) => !keep.has(id))
    if (!remove.length) {
      window.alert("All crops are already selected — nothing to remove.")
      return
    }
    if (!window.confirm(`Keep ${keep.size} · remove ${remove.length} unselected crop(s)?`)) return
    await this._galleryDeleteIds(remove)
  }

  async _galleryDeleteIds(ids) {
    const deleteUrl = this.hasDeleteUrlValue ? this.deleteUrlValue : null
    if (!deleteUrl) {
      window.alert("Delete URL missing.")
      return
    }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    ids.forEach((id) => body.append("symbol_ids[]", id))
    try {
      const res = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": token || "",
          Accept: "text/vnd.turbo-stream.html, application/json, text/html",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
        },
        body: body.toString(),
        credentials: "same-origin"
      })
      const html = await res.text()
      if (window.Turbo?.renderStreamMessage && html.includes("turbo-stream")) {
        window.Turbo.renderStreamMessage(html)
      }
      // Remove tiles from open gallery
      ids.forEach((id) => {
        this.galleryGridTarget?.querySelector(`.cd-gallery-tile[data-instance-id="${id}"]`)?.remove()
        this._galleryCheckedIds?.delete(id)
      })
      if (this._gallerySelectedId && ids.includes(String(this._gallerySelectedId))) {
        this._gallerySelectedId = null
        this._clearGalleryPageImage()
        if (this.hasGalleryDetailTarget) this.galleryDetailTarget.hidden = true
      }
      this.syncGallerySelectionUi()
      if (this.hasGalleryMetaTarget) {
        const left = this.galleryGridTarget?.querySelectorAll(".cd-gallery-tile")?.length || 0
        this.galleryMetaTarget.textContent = `${left} crop(s) remaining · multi-select keep/remove`
      }
    } catch (err) {
      console.warn("[cd-symbols-table] gallery delete failed", err)
      window.alert("Failed to remove crops.")
    }
  }

  selectGalleryTile(event, tileEl = null) {
    const tile = tileEl || event?.currentTarget?.closest?.(".cd-gallery-tile") || event?.currentTarget
    if (!tile) return
    this._galleryRangeAnchorId = tile.dataset.instanceId
    this.galleryGridTarget?.querySelectorAll(".cd-gallery-tile.is-selected")?.forEach((el) => {
      el.classList.remove("is-selected")
    })
    tile.classList.add("is-selected")
    this._gallerySelectedId = tile.dataset.instanceId
    let bbox = {}
    try {
      bbox = JSON.parse(tile.dataset.bbox || "{}")
    } catch (_e) {
      bbox = {}
    }
    this._activeBbox = { ...bbox }
    this._bindCropWindow()

    if (this.hasGalleryDetailTarget) this.galleryDetailTarget.hidden = false
    if (this.hasGalleryDetailImgTarget) {
      // Load only the selected page into the one shared coordinate editor.
      const page = tile.dataset.page || bbox.page_number
      const pagePdf =
        this._galleryDrawingBase && page
          ? `${this._galleryDrawingBase}/native_crop_pdf?page_number=${encodeURIComponent(page)}`
          : null
      this._loadGalleryPageImage(pagePdf)
    }
    if (this.hasGalleryDetailMetaTarget) {
      const lab = (tile.dataset.label || "").trim()
      this.galleryDetailMetaTarget.textContent =
        `Instance #${tile.dataset.instanceId} · page ${tile.dataset.page}` +
        (lab ? ` · ${lab}` : "") +
        " · freeform polygon · wheel zoom"
    }
    this._setGeomFields(bbox)
    if (this.hasGeomLegendIdTarget) this.geomLegendIdTarget.value = tile.dataset.instanceId || ""
    if (this.hasGeomLabelTarget) this.geomLabelTarget.value = tile.dataset.label || ""
    if (this.hasGeomLockLabelTarget) this.geomLockLabelTarget.checked = true
    if (this.hasGeomFormTarget && this._galleryDrawingBase) {
      this.geomFormTarget.action = `${this._galleryDrawingBase}/update_symbol_geometry`
    }
    if (this.hasGeomStatusTarget) {
      this.geomStatusTarget.textContent =
        this._galleryImageReady
          ? "Click vertices · pinch/wheel zoom · space+drag pan · Enter closes · Save"
          : "Loading native PDF…"
    }
    this._drawCropOverlayFromBbox(bbox)
  }

  _clearGalleryPageImage() {
    this._galleryPreviewAbort?.abort()
    this._galleryPreviewAbort = null
    this._galleryPdfRenderTask?.cancel()
    this._galleryPdfRenderTask = null
    this._galleryPdfLoading?.destroy()?.catch(() => {})
    this._galleryPdfLoading = null
    this._galleryPageUrl = null
    this._galleryPageSize = null
    this._galleryImageReady = false
    if (this.hasGalleryDetailImgTarget) {
      this.galleryDetailImgTarget.width = 1
      this.galleryDetailImgTarget.height = 1
    }
    if (this.hasGeomSaveBtnTarget) this.geomSaveBtnTarget.disabled = true
  }

  _galleryPdfRenderer() {
    return import(this.galleryDetailImgTarget.dataset.pdfRendererUrl)
  }

  async _loadGalleryPageImage(url) {
    const canvas = this.galleryDetailImgTarget
    if (url && url === this._galleryPageUrl && this._galleryImageReady) {
      Object.assign(this._activeBbox, this._galleryPageSize)
      this._drawCropOverlayFromBbox(this._activeBbox)
      this._fitCropToView(this._activeBbox)
      return
    }
    this._clearGalleryPageImage()
    if (!url) return
    const request = new AbortController()
    this._galleryPreviewAbort = request
    this._galleryPageUrl = url
    const current = () => request === this._galleryPreviewAbort && !request.signal.aborted && !this.galleryModalTarget.hidden
    try {
      const [response, renderer] = await Promise.all([
        fetch(url, { signal: request.signal, credentials: "same-origin" }),
        this._galleryPdfRenderer()
      ])
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const width = Number(response.headers.get("X-Page-Width"))
      const height = Number(response.headers.get("X-Page-Height"))
      if (!(width > 0 && height > 0)) throw new Error("Original crop coordinates unavailable")
      const bytes = await response.arrayBuffer()
      if (!current()) return
      this._galleryPdfLoading = renderer.openNativePage(bytes, canvas.dataset.pdfWorkerUrl)
      const pdf = await this._galleryPdfLoading.promise
      if (!current()) return
      const page = await pdf.getPage(1)
      if (!current()) return
      // Same native PDF renderer and display bound as the Legends editor.
      // Canvas pixels are independent of the original 600-DPI crop coordinates.
      const base = page.getViewport({ scale: 1 })
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const scale = Math.min((96 / 72) * dpr, 2200 / Math.max(base.width, base.height, 1))
      const viewport = page.getViewport({ scale })
      const buffer = document.createElement("canvas")
      buffer.width = Math.ceil(viewport.width)
      buffer.height = Math.ceil(viewport.height)
      const render = page.render({ canvasContext: buffer.getContext("2d"), viewport })
      this._galleryPdfRenderTask = render
      await render.promise
      if (!current() || render !== this._galleryPdfRenderTask) return
      canvas.width = buffer.width
      canvas.height = buffer.height
      canvas.getContext("2d").drawImage(buffer, 0, 0)
      this._galleryPageSize = { page_width_px: width, page_height_px: height }
      Object.assign(this._activeBbox, this._galleryPageSize)
      this._galleryImageReady = true
      this._drawCropOverlayFromBbox(this._activeBbox)
      this._fitCropToView(this._activeBbox)
      if (this.hasGeomSaveBtnTarget) this.geomSaveBtnTarget.disabled = !!(this._galleryRelabelInFlight || this._galleryGeometrySaving)
      if (this.hasGeomStatusTarget) {
        this.geomStatusTarget.textContent = "Click vertices · pinch/wheel zoom · space+drag pan · Enter closes · Save"
      }
    } catch (error) {
      if (!current()) return
      this._clearGalleryPageImage()
      if (this.hasGeomStatusTarget) this.geomStatusTarget.textContent = "Page preview unavailable. Select the instance again to retry."
    }
  }

  _setGeomFields(bbox) {
    const x = bbox.x ?? ""
    const y = bbox.y ?? ""
    const w = bbox.width ?? ""
    const h = bbox.height ?? ""
    if (this.hasGeomXTarget) this.geomXTarget.value = x
    if (this.hasGeomYTarget) this.geomYTarget.value = y
    if (this.hasGeomWTarget) this.geomWTarget.value = w
    if (this.hasGeomHTarget) this.geomHTarget.value = h
    if (this.hasFormGeomXTarget) this.formGeomXTarget.value = x
    if (this.hasFormGeomYTarget) this.formGeomYTarget.value = y
    if (this.hasFormGeomWTarget) this.formGeomWTarget.value = w
    if (this.hasFormGeomHTarget) this.formGeomHTarget.value = h
    if (this.hasFormPolygonJsonTarget) {
      this.formPolygonJsonTarget.value = JSON.stringify(this._cropPoints || [])
    }
  }

  _bindCropWindow() {
    window.addEventListener("mousemove", this._onCropWinMove)
    window.addEventListener("mouseup", this._onCropWinUp)
    window.addEventListener("keydown", this._onCropKeyDown)
    window.addEventListener("keyup", this._onCropKeyUp)
    this._bindCropPinch()
  }

  _unbindCropWindow() {
    window.removeEventListener("mousemove", this._onCropWinMove)
    window.removeEventListener("mouseup", this._onCropWinUp)
    window.removeEventListener("keydown", this._onCropKeyDown)
    window.removeEventListener("keyup", this._onCropKeyUp)
    this._unbindCropPinch()
  }

  _bindCropPinch() {
    if (!this.hasCropStageTarget) return
    this._unbindCropPinch()
    this._onCropPinchStart = (e) => this._cropPinchStart(e)
    this._onCropPinchMove = (e) => this._cropPinchMove(e)
    this._onCropPinchEnd = (e) => this._cropPinchEnd(e)
    this.cropStageTarget.addEventListener("touchstart", this._onCropPinchStart, { passive: false })
    this.cropStageTarget.addEventListener("touchmove", this._onCropPinchMove, { passive: false })
    this.cropStageTarget.addEventListener("touchend", this._onCropPinchEnd)
    this.cropStageTarget.addEventListener("touchcancel", this._onCropPinchEnd)
  }

  _unbindCropPinch() {
    if (!this.hasCropStageTarget) return
    if (this._onCropPinchStart) {
      this.cropStageTarget.removeEventListener("touchstart", this._onCropPinchStart)
      this.cropStageTarget.removeEventListener("touchmove", this._onCropPinchMove)
      this.cropStageTarget.removeEventListener("touchend", this._onCropPinchEnd)
      this.cropStageTarget.removeEventListener("touchcancel", this._onCropPinchEnd)
    }
    this._cropPinch = null
  }

  _cropTouchDist(touches) {
    return Math.hypot(
      touches[1].clientX - touches[0].clientX,
      touches[1].clientY - touches[0].clientY
    )
  }

  _cropTouchMid(touches, rect) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
      y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top
    }
  }

  _cropPinchStart(event) {
    if (!this.hasCropStageTarget || event.touches.length !== 2) return
    event.preventDefault()
    const rect = this.cropStageTarget.getBoundingClientRect()
    const mid = this._cropTouchMid(event.touches, rect)
    this._cropPinch = {
      dist: this._cropTouchDist(event.touches),
      scale: this._cropScale,
      midX: mid.x,
      midY: mid.y,
      panX: this._cropPanX,
      panY: this._cropPanY
    }
    this._cropDragVertex = -1
    this._cropPanning = false
  }

  _cropPinchMove(event) {
    if (!this._cropPinch || event.touches.length !== 2) return
    event.preventDefault()
    const dist = this._cropTouchDist(event.touches)
    if (this._cropPinch.dist < 8) return
    const factor = dist / this._cropPinch.dist
    const next = Math.min(12, Math.max(0.08, this._cropPinch.scale * factor))
    const rect = this.cropStageTarget.getBoundingClientRect()
    const mid = this._cropTouchMid(event.touches, rect)
    const worldX = (this._cropPinch.midX - this._cropPinch.panX) / this._cropPinch.scale
    const worldY = (this._cropPinch.midY - this._cropPinch.panY) / this._cropPinch.scale
    this._cropScale = next
    this._cropPanX = mid.x - worldX * this._cropScale
    this._cropPanY = mid.y - worldY * this._cropScale
    this._applyCropTransform()
    this._renderCropPoly()
  }

  _cropPinchEnd(event) {
    if (event.touches && event.touches.length >= 2) return
    this._cropPinch = null
  }

  _pageDims() {
    const pw = Number(this._activeBbox?.page_width_px) || this.galleryDetailImgTarget?.naturalWidth || 1
    const ph = Number(this._activeBbox?.page_height_px) || this.galleryDetailImgTarget?.naturalHeight || 1
    return { w: pw, h: ph }
  }

  _clampPage(p) {
    const { w, h } = this._pageDims()
    return { x: Math.min(Math.max(0, p.x), w), y: Math.min(Math.max(0, p.y), h) }
  }

  _eventToPage(event) {
    if (!this.hasCropStageTarget) return null
    const stage = this.cropStageTarget.getBoundingClientRect()
    const sx = event.clientX - stage.left
    const sy = event.clientY - stage.top
    return this._clampPage({
      x: (sx - this._cropPanX) / this._cropScale,
      y: (sy - this._cropPanY) / this._cropScale
    })
  }

  _applyCropTransform() {
    if (!this.hasCropViewportTarget) return
    this.cropViewportTarget.style.transform =
      `translate(${this._cropPanX}px, ${this._cropPanY}px) scale(${this._cropScale})`
    this.cropViewportTarget.style.transformOrigin = "0 0"
    if (this.hasCropZoomLabelTarget) {
      this.cropZoomLabelTarget.textContent = `${Math.round(this._cropScale * 100)}%`
    }
  }

  _fitCropToView(focusBbox) {
    if (!this.hasCropStageTarget || !this.hasGalleryDetailImgTarget) return
    const stage = this.cropStageTarget.getBoundingClientRect()
    const { w, h } = this._pageDims()
    if (stage.width < 10 || stage.height < 10 || !w || !h) return
    // Prefer framing current symbol AABB with pad, else full page.
    let vx = 0
    let vy = 0
    let vw = w
    let vh = h
    if (focusBbox?.width > 0 && focusBbox?.height > 0) {
      const pad = 1.5
      vx = Math.max(0, focusBbox.x - focusBbox.width * pad)
      vy = Math.max(0, focusBbox.y - focusBbox.height * pad)
      vw = Math.min(w - vx, focusBbox.width * (1 + 2 * pad))
      vh = Math.min(h - vy, focusBbox.height * (1 + 2 * pad))
    }
    const s = Math.min(stage.width / vw, stage.height / vh) * 0.9
    this._cropScale = Math.max(0.08, Math.min(8, s))
    this._cropPanX = stage.width / 2 - (vx + vw / 2) * this._cropScale
    this._cropPanY = stage.height / 2 - (vy + vh / 2) * this._cropScale
    this._applyCropTransform()
  }

  _polyFromBbox(bbox) {
    if (!bbox || !(bbox.width > 0) || !(bbox.height > 0)) return []
    return [
      { x: bbox.x, y: bbox.y },
      { x: bbox.x + bbox.width, y: bbox.y },
      { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
      { x: bbox.x, y: bbox.y + bbox.height }
    ]
  }

  _aabbFromPoints(pts) {
    if (!pts?.length) return null
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    pts.forEach((p) => {
      minX = Math.min(minX, p.x)
      minY = Math.min(minY, p.y)
      maxX = Math.max(maxX, p.x)
      maxY = Math.max(maxY, p.y)
    })
    const width = maxX - minX
    const height = maxY - minY
    if (width < 2 || height < 2) return null
    return {
      x: minX,
      y: minY,
      width,
      height,
      page_width_px: this._activeBbox?.page_width_px,
      page_height_px: this._activeBbox?.page_height_px
    }
  }

  _renderCropPoly() {
    const { w, h } = this._pageDims()
    if (this.hasGalleryDetailImgTarget) {
      this.galleryDetailImgTarget.style.width = `${w}px`
      this.galleryDetailImgTarget.style.height = `${h}px`
    }
    if (this.hasCropSvgTarget) {
      this.cropSvgTarget.setAttribute("viewBox", `0 0 ${w} ${h}`)
      this.cropSvgTarget.setAttribute("width", String(w))
      this.cropSvgTarget.setAttribute("height", String(h))
    }
    const pts = this._cropPoints || []
    const d =
      pts.length === 0
        ? ""
        : pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ") +
          (this._cropClosed ? " Z" : "")
    if (this.hasCropPolyPathTarget) {
      this.cropPolyPathTarget.setAttribute("d", d)
      this.cropPolyPathTarget.style.display = pts.length ? "" : "none"
    }
    if (this.hasCropPolyLineTarget) {
      this.cropPolyLineTarget.setAttribute("d", d)
      this.cropPolyLineTarget.style.display = pts.length ? "" : "none"
    }
    if (this.hasCropPolyDotsTarget) {
      this.cropPolyDotsTarget.innerHTML = ""
      pts.forEach((p, i) => {
        const c = document.createElementNS("http://www.w3.org/2000/svg", "circle")
        c.setAttribute("cx", String(p.x))
        c.setAttribute("cy", String(p.y))
        c.setAttribute("r", String(Math.max(3, 7 / this._cropScale)))
        if (i === 0 && !this._cropClosed) c.setAttribute("class", "is-first")
        this.cropPolyDotsTarget.appendChild(c)
      })
    }
  }

  _syncCropBboxFields() {
    const box = this._aabbFromPoints(this._cropPoints)
    if (!box) return
    this._activeBbox = { ...this._activeBbox, ...box }
    this._setGeomFields(this._activeBbox)
  }

  cropPointerDown(event) {
    if (!this._gallerySelectedId || !this.hasCropStageTarget || !this._galleryImageReady) return
    if (event.button === 1 || event.button === 2 || this._cropSpace || event.altKey) {
      event.preventDefault()
      this._cropPanning = true
      this._cropPanStart = {
        x: event.clientX,
        y: event.clientY,
        panX: this._cropPanX,
        panY: this._cropPanY
      }
      this.cropStageTarget.classList.add("is-panning")
      return
    }
    if (event.button != null && event.button !== 0) return
    event.preventDefault()
    const pagePt = this._eventToPage(event)
    if (!pagePt) return

    if (this._cropClosed) {
      const hit = this._hitCropVertex(pagePt, 16 / this._cropScale)
      if (hit >= 0) {
        this._cropDragVertex = hit
        return
      }
      this._cropPoints = [pagePt]
      this._cropClosed = false
      this._renderCropPoly()
      if (this.hasGeomStatusTarget) this.geomStatusTarget.textContent = "1 vertex — continue freeform polygon"
      return
    }

    if (this._cropPoints.length >= 3) {
      const f = this._cropPoints[0]
      if (Math.hypot(pagePt.x - f.x, pagePt.y - f.y) < 18 / this._cropScale) {
        this.cropClosePoly()
        return
      }
    }

    this._cropPoints.push(pagePt)
    this._renderCropPoly()
    this._syncCropBboxFields()
    if (this.hasGeomStatusTarget) {
      this.geomStatusTarget.textContent =
        this._cropPoints.length < 3
          ? `${this._cropPoints.length} vertex — need ≥3`
          : `${this._cropPoints.length} verts — click start / Enter to close`
    }
  }

  cropPointerMove(event) {
    if (this._cropPanning && this._cropPanStart) {
      event.preventDefault()
      this._cropPanX = this._cropPanStart.panX + (event.clientX - this._cropPanStart.x)
      this._cropPanY = this._cropPanStart.panY + (event.clientY - this._cropPanStart.y)
      this._applyCropTransform()
      return
    }
    if (this._cropDragVertex >= 0) {
      event.preventDefault()
      const pagePt = this._eventToPage(event)
      if (!pagePt) return
      this._cropPoints[this._cropDragVertex] = pagePt
      this._renderCropPoly()
      this._syncCropBboxFields()
    }
  }

  cropPointerUp(_event) {
    if (this._cropPanning) {
      this._cropPanning = false
      this._cropPanStart = null
      if (!this._cropSpace) this.cropStageTarget?.classList.remove("is-panning")
    }
    if (this._cropDragVertex >= 0) {
      this._cropDragVertex = -1
      if (this.hasGeomStatusTarget) {
        this.geomStatusTarget.textContent = "Polygon updated — Save crop & label"
      }
    }
  }

  cropWheel(event) {
    if (!this.hasCropStageTarget) return
    event.preventDefault()
    const rect = this.cropStageTarget.getBoundingClientRect()
    const mx = event.clientX - rect.left
    const my = event.clientY - rect.top
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12
    const next = Math.min(12, Math.max(0.08, this._cropScale * factor))
    const worldX = (mx - this._cropPanX) / this._cropScale
    const worldY = (my - this._cropPanY) / this._cropScale
    this._cropScale = next
    this._cropPanX = mx - worldX * this._cropScale
    this._cropPanY = my - worldY * this._cropScale
    this._applyCropTransform()
    this._renderCropPoly()
  }

  cropKeyDown(event) {
    if (!this._gallerySelectedId || !this.hasGalleryDetailTarget || this.galleryDetailTarget.hidden) return
    if (event.key === " " || event.code === "Space") {
      this._cropSpace = true
      this.cropStageTarget?.classList.add("is-panning")
      event.preventDefault()
    }
    if ((event.key === "Enter" || event.key === "e") && this._cropPoints.length >= 3) {
      event.preventDefault()
      this.cropClosePoly()
    }
    if ((event.key === "Backspace" || event.key === "Delete") && this._cropPoints.length) {
      event.preventDefault()
      this.cropUndo()
    }
  }

  cropKeyUp(event) {
    if (event.key === " " || event.code === "Space") {
      this._cropSpace = false
      this.cropStageTarget?.classList.remove("is-panning")
    }
  }

  cropClosePoly(event) {
    event?.preventDefault?.()
    if (this._cropPoints.length < 3) {
      if (this.hasGeomStatusTarget) this.geomStatusTarget.textContent = "Need ≥3 vertices."
      return
    }
    this._cropClosed = true
    this._renderCropPoly()
    this._syncCropBboxFields()
    if (this.hasGeomStatusTarget) {
      this.geomStatusTarget.textContent = "Polygon closed — Save crop & label (or drag vertices)"
    }
  }

  cropUndo(event) {
    event?.preventDefault?.()
    if (this._cropClosed) {
      this._cropClosed = false
    } else if (this._cropPoints.length) {
      this._cropPoints.pop()
    }
    this._renderCropPoly()
    this._syncCropBboxFields()
  }

  cropClear(event) {
    event?.preventDefault?.()
    this._cropPoints = []
    this._cropClosed = false
    this._renderCropPoly()
    if (this.hasGeomStatusTarget) {
      this.geomStatusTarget.textContent = "Cleared — click to place freeform vertices"
    }
  }

  _hitCropVertex(pagePt, radius) {
    for (let i = 0; i < this._cropPoints.length; i++) {
      const p = this._cropPoints[i]
      if (Math.hypot(pagePt.x - p.x, pagePt.y - p.y) <= radius) return i
    }
    return -1
  }

  _drawCropOverlayFromBbox(bbox) {
    // Seed freeform poly from existing rectangle (or prior poly in overlay_paths).
    const paths = bbox?.overlay_paths
    if (Array.isArray(paths) && paths[0]?.points?.length >= 3) {
      const baseX = Number(bbox.x) || 0
      const baseY = Number(bbox.y) || 0
      const bw = Number(bbox.width) || 1
      const bh = Number(bbox.height) || 1
      this._cropPoints = paths[0].points.map((pt) => {
        const nx = Number(Array.isArray(pt) ? pt[0] : pt.x)
        const ny = Number(Array.isArray(pt) ? pt[1] : pt.y)
        // Normalized 0–1 points → page; absolute if already large.
        if (nx <= 1.5 && ny <= 1.5) {
          return { x: baseX + nx * bw, y: baseY + ny * bh }
        }
        return { x: nx, y: ny }
      })
      this._cropClosed = true
    } else {
      this._cropPoints = this._polyFromBbox(bbox)
      this._cropClosed = this._cropPoints.length >= 3
    }
    this._renderCropPoly()
    this._syncCropBboxFields()
  }

  geometrySaving(event) {
    this._galleryGeometrySaving = true
    this.syncGallerySelectionUi()
    if (this.hasGeomStatusTarget) this.geomStatusTarget.textContent = "Saving crop…"
    if (this.hasGeomSaveBtnTarget) this.geomSaveBtnTarget.disabled = true
  }

  geometrySaved(event) {
    this._galleryGeometrySaving = false
    this.syncGallerySelectionUi()
    if (this.hasGeomSaveBtnTarget) this.geomSaveBtnTarget.disabled = !this._galleryImageReady
    const detail = event?.detail
    // Turbo submit-end: success → refresh tile + pending banner already streamed
    const statusEl = document.getElementById("gallery_geom_status")
    const thumb = statusEl?.dataset?.thumbUrl
    const id = statusEl?.dataset?.legendId || this._gallerySelectedId
    let bbox = null
    try {
      bbox = statusEl?.dataset?.bbox ? JSON.parse(statusEl.dataset.bbox) : null
    } catch (_e) {
      bbox = null
    }
    const newLabel = statusEl?.dataset?.label
    if (id && thumb) {
      const bust = `${thumb}${thumb.includes("?") ? "&" : "?"}t=${Date.now()}`
      const tile = this._galleryGridEl()?.querySelector(`.cd-gallery-tile[data-instance-id="${id}"]`)
      if (tile) {
        tile.dataset.thumbUrl = bust
        tile.classList.remove("is-broken")
        if (newLabel != null && newLabel !== "") tile.dataset.label = newLabel
        const img = tile.querySelector("img")
        if (img) {
          img.style.display = ""
          img.src = bust
        }
        if (bbox) tile.dataset.bbox = JSON.stringify(bbox)
        const cap = tile.querySelector(".cd-gallery-tile-cap")
        if (cap && newLabel) {
          const page = tile.dataset.page || "?"
          const lab = newLabel.length > 14 ? newLabel.slice(0, 12) + "…" : newLabel
          cap.textContent = `p.${page} · ${lab}`
        }
      }
      // The saved crop is a tile thumbnail, never the page-coordinate editor.
      if (String(id) === String(this._gallerySelectedId) && !this.galleryModalTarget.hidden) {
        if (this.hasGeomLabelTarget && newLabel) this.geomLabelTarget.value = newLabel
        if (bbox) {
          this._activeBbox = { ...this._activeBbox, ...bbox }
          this._setGeomFields(this._activeBbox)
          this._drawCropOverlayFromBbox(this._activeBbox)
        }
      }
    }
    if (this.hasGeomStatusTarget && !statusEl) {
      this.geomStatusTarget.textContent = "Saved · re-mark job pending — take-off locked until done."
    }
    // Poll pending banner if stream missed
    this._refreshPendingBanner()
  }

  async _refreshPendingBanner() {
    // Light poll: reload page fragment via fetch of drawing show is heavy —
    // status text already updated by turbo stream when present.
  }

  // Legacy fetch save (fallback if form not used).
  // Debounce rapid Save / drag-end submits so one re-mark job coalesces per edit burst.
  async saveGeometry(event) {
    event?.preventDefault?.()
    if (this._geomSaveTimer) {
      clearTimeout(this._geomSaveTimer)
      this._geomSaveTimer = null
    }
    const bboxKey = JSON.stringify(this._activeBbox || {})
    if (this._lastGeomSaveKey === bboxKey && this._geomSaveInFlight) {
      if (this.hasGeomStatusTarget) {
        this.geomStatusTarget.textContent = "Save already in flight for this crop…"
      }
      return
    }
    this._geomSaveTimer = setTimeout(() => {
      this._geomSaveTimer = null
      this._flushGeometrySave(bboxKey)
    }, 400)
  }

  _flushGeometrySave(bboxKey) {
    if (!this.hasGeomFormTarget) return
    this._setGeomFields(this._activeBbox || {})
    this._lastGeomSaveKey = bboxKey
    this._geomSaveInFlight = true
    if (this.hasGeomStatusTarget) this.geomStatusTarget.textContent = "Saving crop…"
    try {
      if (typeof this.geomFormTarget.requestSubmit === "function") {
        this.geomFormTarget.requestSubmit()
      } else {
        this.geomFormTarget.submit()
      }
    } finally {
      // Allow next distinct bbox after a short cool-down (Turbo will re-enable button).
      setTimeout(() => {
        this._geomSaveInFlight = false
      }, 800)
    }
  }

  closeGallery(event) {
    event?.preventDefault?.()
    this._gallerySession = null
    this._galleryRequest?.abort()
    this._galleryRequest = null
    this._clearGalleryPageImage()
    if (!this.hasGalleryModalTarget) return
    this.galleryModalTarget.hidden = true
    document.body.classList.remove("cd-gallery-open")
    // Drop loaded group so next Review starts clean (no stale tiles / thumbs).
    const host = this._galleryHostEl()
    if (host) {
      host.removeAttribute("src")
      host.innerHTML =
        `<div class="cd-gallery-grid" data-cd-symbols-table-target="galleryGrid" aria-label="Instance tiles">` +
        `<p class="cd-dim cd-gallery-empty-hint">Open <strong>Review</strong> on a row to load that group only.</p></div>`
    } else if (this.hasGalleryGridTarget) {
      this.galleryGridTarget.innerHTML = ""
    }
    if (this.hasGalleryDetailTarget) this.galleryDetailTarget.hidden = true
    this._gallerySelectedId = null
    this._galleryCheckedIds = new Set()
    this._galleryGroupIds = []
    this._cropPoints = []
    this._cropClosed = false
    this._unbindCropWindow()
    this._galleryThumbObserver?.disconnect()
    this._galleryThumbObserver = null
  }

  _galleryGridEl() {
    if (this.hasGalleryGridTarget) return this.galleryGridTarget
    return this.element.querySelector(".cd-gallery-grid")
  }

  // Promote a step chip label into the row's Master input and save (reviewed).
  promoteStep(event) {
    event?.preventDefault?.()
    const btn = event?.currentTarget
    if (!btn || btn.disabled) return
    const label = (btn.dataset.label || "").trim()
    const tr = btn.closest("tr")
    if (!tr || !label) return
    const input = tr.querySelector("[data-cd-symbols-table-target='labelInput']")
    const form = tr.querySelector("[data-cd-symbols-table-target='labelForm']")
    if (!input || !form) return
    input.value = label
    input.dataset.lastSaved = ""
    this.submitLabelForm({ currentTarget: input, type: "change" })
  }

  acceptSelectedMaster(event) {
    event?.preventDefault?.()
    this.postAccept(this.selectedIds(), { scope: "selected" })
  }

  acceptAllMaster(event) {
    event?.preventDefault?.()
    const all = this.allRowChecks().map((el) => el.value).filter(Boolean)
    this.postAccept(all, { scope: "all" })
  }

  postAccept(idCsvList, { scope }) {
    const url = this.hasAcceptUrlValue ? this.acceptUrlValue : null
    if (!url) {
      console.warn("[cd-symbols-table] acceptUrl missing")
      return
    }
    const ids = (idCsvList || []).flatMap((csv) => String(csv).split(",")).map((s) => s.trim()).filter(Boolean)
    if (!ids.length) return
    if (!window.confirm(`Accept Master labels for ${scope === "all" ? "ALL" : "selected"} groups (${ids.length} instances)?`)) {
      return
    }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    ids.forEach((id) => body.append("symbol_ids[]", id))
    body.append("mode", "reviewed")
    fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token || "",
        Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
      },
      body: body.toString(),
      credentials: "same-origin"
    })
      .then(async (res) => {
        const html = await res.text()
        if (window.Turbo?.renderStreamMessage && html.includes("turbo-stream")) {
          window.Turbo.renderStreamMessage(html)
        } else if (res.ok) {
          window.location.reload()
        } else {
          console.warn("[cd-symbols-table] accept failed", res.status)
        }
      })
      .catch((err) => console.warn("[cd-symbols-table] accept error", err))
  }

  // —— Selection (delegated change handler — works even after DT thead clone) ——

  onCheckboxChange(event) {
    const target = event?.target
    if (!target || target.type !== "checkbox") return

    if (target.classList.contains("cd-symbol-check-all")) {
      this.toggleSelectAll(event)
      return
    }
    if (target.classList.contains("cd-symbol-row-check")) {
      this.rowCheckChanged(event)
    }
  }

  visibleRowChecks() {
    if (!this.dataTable) {
      return this.rowCheckTargets.filter((el) => el.isConnected)
    }
    const nodes = this.dataTable.rows({ search: "applied", page: "current" }).nodes().toArray()
    return nodes.flatMap((tr) => [...tr.querySelectorAll(".cd-symbol-row-check")])
  }

  allRowChecks() {
    return this.hasRowCheckTarget ? this.rowCheckTargets : [...this.element.querySelectorAll(".cd-symbol-row-check")]
  }

  selectedIds() {
    return this.allRowChecks()
      .filter((el) => el.checked && !el.disabled)
      .map((el) => el.value)
      .filter(Boolean)
  }

  selectedInstanceCount() {
    return this.selectedIds().reduce((sum, csv) => {
      const n = csv.split(",").map((s) => s.trim()).filter(Boolean).length
      return sum + n
    }, 0)
  }

  toggleSelectAll(event) {
    const checked = !!event?.target?.checked
    this.visibleRowChecks().forEach((el) => {
      if (!el.disabled) el.checked = checked
    })
    this.selectAllCheckboxes().forEach((el) => {
      el.checked = checked
      el.indeterminate = false
    })
    this.syncSelectionUi()
  }

  rowCheckChanged(_event) {
    this.syncSelectionUi()
  }

  selectAllCheckboxes() {
    return [...this.element.querySelectorAll(".cd-symbol-check-all")]
  }

  syncSelectionUi() {
    const visible = this.visibleRowChecks().filter((el) => !el.disabled)
    const checkedVisible = visible.filter((el) => el.checked)
    const allChecked = visible.length > 0 && checkedVisible.length === visible.length
    const partial = checkedVisible.length > 0 && checkedVisible.length < visible.length
    this.selectAllCheckboxes().forEach((el) => {
      el.checked = allChecked
      el.indeterminate = partial
    })

    const groups = this.selectedIds().length
    const instances = this.selectedInstanceCount()
    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent =
        groups === 0
          ? "0 selected"
          : `${groups} group${groups === 1 ? "" : "s"} · ${instances} instance${instances === 1 ? "" : "s"} selected`
    }
    if (this.hasBulkDeleteBtnTarget) {
      this.bulkDeleteBtnTarget.disabled = groups === 0 || this.bulkDeleteBtnTarget.dataset.forceDisabled === "true"
    }
  }

  prepareBulkDelete(event) {
    const ids = this.selectedIds()
    if (ids.length === 0) {
      event.preventDefault()
      window.alert("Select at least one label group to delete.")
      return
    }

    const instances = this.selectedInstanceCount()
    const ok = window.confirm(
      `Delete ${ids.length} label group${ids.length === 1 ? "" : "s"} ` +
        `(${instances} symbol instance${instances === 1 ? "" : "s"})? ` +
        "This cannot be undone — take-off will no longer count them."
    )
    if (!ok) {
      event.preventDefault()
      return
    }

    if (!this.hasBulkFieldsTarget) return
    this.bulkFieldsTarget.innerHTML = ""
    const flat = new Set()
    ids.forEach((csv) => {
      csv.split(",").forEach((part) => {
        const t = part.trim()
        if (t) flat.add(t)
      })
    })
    flat.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "symbol_ids[]"
      input.value = id
      this.bulkFieldsTarget.appendChild(input)
    })
  }

  // —— DataTables lifecycle ——

  destroyDataTable() {
    this.destroyItemSelect2()
    const table = this.tableEl()
    if (table && window.$) {
      try {
        $(table).off(".cdSymbols")
      } catch (_error) {
        // ignore
      }
    }
    this.destroyTableNode(table)
    this.element.querySelectorAll("table").forEach((node) => this.destroyTableNode(node))
    this.dataTable = null
  }

  destroyTableNode(table) {
    if (!table || !window.$?.fn?.DataTable) return
    try {
      if ($.fn.DataTable.isDataTable(table)) {
        $(table).DataTable().destroy()
      }
      table.querySelectorAll("thead tr.cd-dt-col-filters").forEach((row) => row.remove())
    } catch (_error) {
      // ignore
    }
    this.destroyOrphansByTableId(table.id)
  }

  destroyOrphansByTableId(tableId) {
    if (!tableId || !window.$?.fn?.dataTable) return
    try {
      const settings = $.fn.dataTable.settings
      if (!settings || !settings.length) return
      for (let i = settings.length - 1; i >= 0; i -= 1) {
        const entry = settings[i]
        const node = entry?.nTable || entry?.nTableWrapper?.querySelector?.("table")
        if (!node) continue
        if (node.id !== tableId && node !== this.tableEl()) continue
        try {
          if (node.isConnected && $.fn.DataTable.isDataTable(node)) {
            $(node).DataTable().destroy()
          } else {
            settings.splice(i, 1)
          }
        } catch (_error) {
          try {
            settings.splice(i, 1)
          } catch (_spliceError) {
            // ignore
          }
        }
      }
    } catch (_error) {
      // ignore
    }
  }

  tableEl() {
    // Scroll headers clone the table's Stimulus target attribute too.
    if (this.dataTable) return this.dataTable.table().node()
    if (this.hasTableTarget) return this.tableTarget
    return this.element.querySelector("table")
  }
}
